import { ChangeEvent, FC } from "react";
import clsx from "clsx";
import { useFormContext } from "react-hook-form";
import { Box, Divider, Tabs, Typography } from "@material-ui/core";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { roiCalculatorStyles } from "../../../../../ThemeProvider";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { StyledTab } from "../../../CompoundFeedWizard/utils";
import { RoiFormStageDescriptor } from "../../../../helpers/roiStageData";
import { RoiCalculatorFormData } from "../../../../models/RoiCalculator";

interface FeedCostsSectionProps {
  stages: RoiFormStageDescriptor[];
  activeTab: number;
  onTabChange: (event: ChangeEvent<unknown>, value: number) => void;
  descriptionId: string;
  readOnly: boolean;
}

const FeedCostsSection: FC<FeedCostsSectionProps> = ({
  stages,
  activeTab,
  onTabChange,
  descriptionId,
  readOnly,
}) => {
  const intl = useIntl();
  const roiClasses = roiCalculatorStyles();
  const { errors } = useFormContext<RoiCalculatorFormData>();

  return (
    <>
      {stages.length > 0 && (
        <>
          <Divider className={roiClasses.divider} />

          <Box className={roiClasses.sectionColumn}>
            <Box className={roiClasses.sectionHeader}>
              <Typography variant="h6" className={roiClasses.sectionTitle}>
                {intl.formatMessage({
                  id: "SUSTELL.ROI.FEED_COSTS.TITLE",
                })}
              </Typography>
              <Typography
                variant="body2"
                className={roiClasses.sectionDescription}
              >
                {intl.formatMessage({ id: descriptionId })}
              </Typography>
            </Box>
          </Box>

          {stages.length > 1 && (
            <Box className={roiClasses.tabsWrapper}>
              <Tabs
                value={activeTab}
                onChange={onTabChange}
                className={roiClasses.tabs}
              >
                {stages.map((stage, index) => {
                  const isTabError = Boolean(
                    errors?.stages?.[index]?.baselineInputs?.feedCost ||
                      errors?.stages?.[index]?.interventionInputs?.feedCost
                  );
                  return (
                    <StyledTab
                      key={stage.stageId ?? index}
                      label={stage.stageName}
                      className={clsx(
                        roiClasses.stageTab,
                        isTabError && roiClasses.stageTabError
                      )}
                    />
                  );
                })}
              </Tabs>
            </Box>
          )}
        </>
      )}

      {stages.map((stage, idx) => (
        <div
          key={stage.stageId ?? idx}
          className={roiClasses.splitColumnsContainer}
          style={{ display: activeTab === idx ? "flex" : "none" }}
        >
          <Box className={roiClasses.splitColumn}>
            <Typography
              variant="subtitle1"
              className={roiClasses.stageSubtitle}
            >
              {intl.formatMessage({
                id: "SUSTELL.ROI.FEED_COSTS.BASELINE",
              })}
            </Typography>
            <ReactHookDsmInput
              name={`stages.${idx}.baselineInputs.feedCost`}
              label={intl.formatMessage({
                id: "SUSTELL.ROI.FEED_COSTS.AVG_FEED_COST",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.ROI.FEED_COSTS.AVG_FEED_COST.PLACEHOLDER",
              })}
              type="number"
              required
              disabled={readOnly}
            />
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box className={roiClasses.splitColumn}>
            <Typography
              variant="subtitle1"
              className={roiClasses.stageSubtitle}
            >
              {intl.formatMessage({
                id: "SUSTELL.ROI.FEED_COSTS.INTERVENTION",
              })}
            </Typography>
            <ReactHookDsmInput
              name={`stages.${idx}.interventionInputs.feedCost`}
              label={intl.formatMessage({
                id: "SUSTELL.ROI.FEED_COSTS.AVG_FEED_COST",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.ROI.FEED_COSTS.AVG_FEED_COST.PLACEHOLDER",
              })}
              type="number"
              required
              disabled={readOnly}
            />
          </Box>
        </div>
      ))}
    </>
  );
};

export default FeedCostsSection;
